import React, { useMemo } from "react";
import { GENERATOR_CODES } from "@csk/core";
import {
  DemoIntervalTrack,
  DemoResponsiveGrid,
  DemoSparkBarChart,
  DemoWorkbenchCard,
  DemoWorkbenchCopy,
  DemoWorkbenchMetricCard,
  DemoWorkbenchPill,
  DemoWorkbenchStack,
  type DemoPillVariant,
} from "@csk/demo-ui";
import {
  BRIDGE_LIFECYCLE_EVENTS,
  BRIDGE_LIFECYCLE_TOTAL_TIME,
  bridgeExposureWindow,
  bridgeModeCount,
  buildBridgeTimeline,
  hasPersistentBridge,
  type BridgeLifecycleMode,
} from "./bridge-lifecycle-panel/bridgeLifecycleModel.js";

const MODE_VARIANT: Record<BridgeLifecycleMode, DemoPillVariant> = {
  revocable: "success",
  leased: "warning",
  persistent: "danger",
};

const MODE_COLOR: Record<BridgeLifecycleMode, string> = {
  revocable: "#22c55e",
  leased: "#f59e0b",
  persistent: "#ef4444",
};

const MODE_SUMMARY: Record<BridgeLifecycleMode, string> = {
  revocable: "exposure: 1 step",
  leased: "exposure: bounded",
  persistent: "exposure: until removal",
};

const BRIDGE_MODES: readonly BridgeLifecycleMode[] = [
  "revocable",
  "leased",
  "persistent",
];

const BridgeLifecyclePanel = (): React.JSX.Element => {
  const events = BRIDGE_LIFECYCLE_EVENTS;
  const { maxKappa, points } = useMemo(
    () => buildBridgeTimeline(events),
    [events],
  );
  const persistentBridgeCount = bridgeModeCount(events, "persistent");

  return (
    <DemoWorkbenchStack>
      <DemoWorkbenchCard compact title="κ(t) — cross-axis coupling over time">
        <DemoSparkBarChart
          ariaLabel="Kappa coupling over bridge lifecycle time"
          bars={points.map((point) => ({
            id: point.time,
            label: `t=${point.time}`,
            value: point.kappa,
            color:
              point.kappa === 0
                ? MODE_COLOR.revocable
                : point.kappa <= 10
                  ? MODE_COLOR.leased
                  : MODE_COLOR.persistent,
            title: `t=${point.time}; κ=${point.kappa}`,
          }))}
          height={80}
          max={maxKappa}
          showLabels={false}
        />
        <div className="mt-1 flex justify-between text-xs text-gray-600">
          <span>t=0</span>
          <span>t={BRIDGE_LIFECYCLE_TOTAL_TIME}</span>
        </div>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard compact title="Bridge exposure windows">
        <DemoWorkbenchStack gap={12}>
          {events.map((event) => {
            const window = bridgeExposureWindow(event);
            return (
              <DemoWorkbenchStack key={`${event.time}-${event.label}`} gap={4}>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <DemoWorkbenchPill variant={MODE_VARIANT[event.mode]}>
                    {event.mode}
                  </DemoWorkbenchPill>
                  <span className="text-gray-400">{event.label}</span>
                  <span className="text-gray-500">
                    {GENERATOR_CODES[event.from]}→{GENERATOR_CODES[event.to]}
                  </span>
                  <span className="text-gray-600">
                    t=[{event.time},
                    {event.mode === "persistent" ? "∞" : window.endTime}]
                  </span>
                </div>
                <DemoIntervalTrack
                  ariaLabel={`${event.label} exposure window`}
                  intervals={[
                    {
                      id: `${event.time}-${event.label}`,
                      label: event.label,
                      startPercent: window.startPercent,
                      widthPercent: Math.max(window.widthPercent, 2),
                      color: `${MODE_COLOR[event.mode]}22`,
                      borderColor: `${MODE_COLOR[event.mode]}80`,
                    },
                  ]}
                  trackColor="#374151"
                />
              </DemoWorkbenchStack>
            );
          })}
        </DemoWorkbenchStack>
      </DemoWorkbenchCard>

      <DemoResponsiveGrid minColumnWidth={150} gap={8}>
        {BRIDGE_MODES.map((mode) => (
          <DemoWorkbenchMetricCard
            key={mode}
            label={mode}
            value={bridgeModeCount(events, mode)}
            color={MODE_COLOR[mode]}
            sub={MODE_SUMMARY[mode]}
          />
        ))}
      </DemoResponsiveGrid>

      <DemoWorkbenchCard compact title="Recommendation">
        {hasPersistentBridge(events) ? (
          <DemoWorkbenchCopy tone="danger">
            {persistentBridgeCount} persistent bridge(s) keep the system away
            from T^n without explicit removal. Consider leased mode.
          </DemoWorkbenchCopy>
        ) : (
          <DemoWorkbenchCopy tone="bright">
            All bridges have bounded exposure. System returns to T^n
            automatically.
          </DemoWorkbenchCopy>
        )}
      </DemoWorkbenchCard>
    </DemoWorkbenchStack>
  );
};

export default BridgeLifecyclePanel;
